/* eslint-disable react/require-default-props */
import {
  Box,
  BoxProps,
  Button,
  Container,
  Drawer,
  IconButton,
  Modal,
  SlideProps,
  styled,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import dayjs from 'dayjs';
import SettingsIcon from '@mui/icons-material/Settings';
import Chart from 'react-apexcharts';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Api from '../../../api';
import ServiceTimeCalculator from '../../../components/calculator/ServiceTimeCalculator';
import TableComponentEditable from '../../../components/shared/TableComponentEditable';
import DeleteButton from '../../../components/list/DeleteButton';
import { getCurrency, getSalesTaxableItems } from '../../../utils/general';
import {
  getCalculatedMarkup,
  getMargin,
  getMarkup,
  getTotalProject
} from '../../../utils/calculatorHelper';
import GeneralDrawer from '../../../components/GeneralDrawer';
// eslint-disable-next-line import/no-cycle
import FormLead from '../FormLead';
import Loader from '../../../components/shared/Loader';
import InformationItem from './components/analysis/InformationItem';
import Tile from './components/analysis/Tile';
import MarginLadderTile from '../../../components/calc-v3/MarginLadderTile';
import RecomputeRatesButton from '../../../components/calc-v3/RecomputeRatesButton';
import SubAnalysisPanel from '../../../components/calc-v3/SubAnalysisPanel';
import { getCalculatorResults } from '../../../utils/calculatorProHelper';
import { IconBox } from '../../../components/shared/sidebars/parts';
import {
  DrawerButton,
  ModalContainer
} from '../../../components/modals/GlobalConfirmModal';
import SortableTable from '../../../components/list/sortableTable';
import lead from '../../../api/lead/lead';
import useInformationDrawerStore from '../../../store/zustand/informationDrawer';
import RegularInput from '../../../components/input/RegularInput';
import PreviousBidAmount from '../../../components/shared/PreviousBidAmount';

interface Props {
  // availability: any;
  mutate: () => void;
  saveStepper: ({ next }: any) => void;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  values: any;
}

const ButtonContainerModal = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '20px'
}));

const Analysis: React.FC<Props> = function Analysis({
  mutate,
  values,
  setValues,
  saveStepper
  // availability
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { palette } = theme;
  const [getCalculator, setGetCalculator] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState<any>({});
  const [presetStep, setPresetStep] = useState<any>(0);
  const [taxes, setTaxes] = useState<any>([]);
  const [staticCosts, setStaticCosts] = useState<any>([]);
  const [updateStaticCost, setUpdateStaticCost] = useState<boolean>(false);
  const [resetStaticCosts, setResetStaticCosts] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openInput, setOpenInput] = useState<boolean>(false);
  const [presetInput, setPresetInput] = useState<string>('');
  const [inputValue, setInputValue] = useState<any>('');
  const [serviceTimeUpdate, setServiceTimeUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (openInput) {
      if (presetInput === 'laborRate') {
        setInputValue(currentData?.information?.employeesLaborRate || '');
      } else if (presetInput === 'suppliesCost') {
        setInputValue(currentData?.pricing?.supplies || '');
      } else if (presetInput === 'equipmentCost') {
        setInputValue(currentData?.pricing?.equipmentCosts || '');
      } else {
        setInputValue('');
      }
    }
  }, [openInput, presetInput]);

  const buyPrice = getTotalProject(values, t);
  const sellPrice = values?.profit
    ? (parseFloat(values.profit || 0) / 100) * buyPrice + buyPrice
    : buyPrice;
  const margin = getMargin(buyPrice, sellPrice);
  const markup = getMarkup(buyPrice, sellPrice);

  // const {
  //   data: currentCalculatorResponse,
  //   error: currentCalculatorError,
  //   mutate: currentCalculatorMutate
  // } = useSWR(
  //   getCalculator
  //     ? {
  //         endDate: values.endDate,
  //         id: values.id,
  //         profit: markup.amount,
  //         startDate: values.startDate,
  //         totalProjectPrice: sellPrice
  //       }
  //     : null,
  //   Api.leadsApi.leadApi.getLeadAnalytics
  // );

  const {
    data: updateStaticCostsData,
    error: updateStaticCostsError,
    mutate: updateStaticCostsMutate
  } = useSWR(
    updateStaticCost
      ? {
          id: values.id,
          staticCosts,
          url: 'api/lead-static-cost-update'
        }
      : null,
    Api.leadsApi.leadApi.updateStaticCostApi
  );

  const {
    data: reseStaticCostsData,
    error: resetStaticCostsError,
    mutate: resetStaticCostsMutate
  } = useSWR(
    resetStaticCosts
      ? {
          id: values.id,

          url: 'api/lead-static-cost-reset'
        }
      : null,
    Api.leadsApi.leadApi.resetLeadValues
  );

  const { data: dataServiceTime, error } = useSWR(
    serviceTimeUpdate
      ? {
          destinations: values.destinations,
          idLead: values.id
        }
      : null,
    Api.LeadServiceTimesApi.updateMultipleLeadServicesTime
  );

  useEffect(() => {
    if (dataServiceTime) {
      setServiceTimeUpdate(false);
    }
  }, [dataServiceTime]);

  const updateCalculator = () => {
    if (!staticCosts.length) {
      setStaticCosts(values?.staticCosts);
    }

    if (values?.staticCosts) {
      const calculatorResults = getCalculatorResults(values);
      if (!taxes.length) {
        setTaxes([]);
      }
      setCurrentData(calculatorResults);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (updateStaticCostsData) {
      setGetCalculator(false);
      setUpdateStaticCost(false);
      mutate();
      setTimeout(() => {
        updateCalculator();
      }, 1000);
    }
  }, [updateStaticCostsData]);

  useEffect(() => {
    if (reseStaticCostsData) {
      setGetCalculator(false);
      setResetStaticCosts(false);
      setTimeout(() => {
        updateCalculator();
        setIsLoading(true);
      }, 1000);
    }
  }, [reseStaticCostsData]);

  const handleOpenInput = (field: string) => {
    setOpenInput(true);
    setPresetInput(field);
  };

  // useEffect(() => {
  //   if (reseStaticCostsData) {
  //     setGetCalculator(false);
  //     setResetStaticCosts(false);
  //     setUpdateStaticCost(false);
  //     mutate();
  //     setTimeout(() => {
  //       updateCalculator();
  //     }, 1000);
  //   }
  // }, [reseStaticCostsData]);

  // useEffect(() => {
  //   if (currentCalculatorError) {
  //     setGetCalculator(false);
  //     setIsLoading(false);
  //     setCurrentData({});
  //   }
  // }, [currentCalculatorError]);

  useEffect(() => {
    if (refreshData) {
      setGetCalculator(true);
      setRefreshData(false);
    }
  }, [refreshData]);

  const handleOpenDrawer = (presetStep = 0) => {
    setPresetStep(presetStep);
    setOpenDrawer(true);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const informationItems = [
    {
      onEdit: () => {
        handleOpenDrawer(2);
      },
      title: t('LEAD.CREATE.ANALYSIS.NUMBER_OF_EMPLOYEES'),
      value: currentData?.information?.employeesNeeded
    },
    {
      onEdit: () => {
        handleOpenDrawer(3);
      },
      title: t('LEAD.CREATE.ANALYSIS.SUPERVISION_HOURS_ASSIGNED'),
      value: Number(currentData?.information?.totalSupervisorTime).toFixed(2)
    },
    {
      legendColor: palette.grey[500],
      onEdit: () => {
        handleOpenInput('laborRate');
      },
      title: t('LEAD.CREATE.ANALYSIS.EMPLOYEE_LABOR_RATE'),
      value: getCurrency(currentData?.information?.employeesLaborRate)
    },
    {
      legendColor: palette.grey[300],
      onEdit: () => {
        handleOpenDrawer(3);
      },
      title: t('LEAD.CREATE.ANALYSIS.SUPERVISION_RATE'),
      value: getCurrency(currentData?.pricing?.supervisionCost)
    },

    {
      legendColor: palette.accent?.[200],
      title: t('LEAD.CREATE.ANALYSIS.GROSS_PROFIT_MARKUP'),
      value:
        values.projectType === 'Ad-Hoc'
          ? `${markup.percent}%`
          : // : currentData?.analytics?.grossMarginAtAccount
            `${markup.percent}%`
    },
    {
      onEdit: () => {
        setOpenModal(true);
      },
      title: `${t('LEAD.CREATE.ANALYSIS.configurationCost')} (${
        currentData?.analytics?.overheadPercent || 0
      })`,
      value: getCurrency(currentData?.pricing?.overheadCost)
    },
    {
      legendColor: '#B57DFF',
      onEdit: () => {
        setOpenModal(true);
      },
      title: t('LEAD.CREATE.ANALYSIS.otherCosts'),
      value: getCurrency(currentData?.pricing?.configurationCosts)
    },
    {
      legendColor: palette.informative[400],
      title: t('LEAD.CREATE.ANALYSIS.logisticCost'),
      value: getCurrency(currentData?.pricing?.logisticCost)
    },
    {
      legendColor: palette.success[500],
      onEdit: () => {
        handleOpenInput('suppliesCost');
      },
      title: t('LEAD.CREATE.ANALYSIS.suppliesCost'),
      value: getCurrency(currentData?.pricing?.supplies)
    },
    {
      legendColor: palette.success[300],
      onEdit: () => {
        handleOpenInput('equipmentCost');
      },
      title: t('LEAD.CREATE.ANALYSIS.equipmentCost'),
      value: getCurrency(currentData?.pricing?.equipmentCosts)
    },
    {
      legendColor: palette.success[100],
      title: t('LEAD.CREATE.ANALYSIS.materialsCost'),
      value: getCurrency(currentData?.pricing?.materialsCost)
    }
  ];

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const totalCosts = currentData?.pricing?.totalCosts || 0;
  const directLaborCost = currentData?.pricing?.directLaborCost || 0;
  const supervisionCost = currentData?.pricing?.supervisionCost || 0;
  const bidAmount = currentData?.pricing?.annualGrossProfit || 0;
  const totalPrice =
    (currentData?.pricing?.annualPrice || 0) -
    (currentData?.pricing?.overheadCost || 0);
  const suppliesCost = currentData?.pricing?.supplies || 0;
  const configurationCosts = currentData?.pricing?.configurationCosts || 0;
  const supplies = currentData?.pricing?.supplies || 0;
  const payrollExpense = currentData?.pricing?.payrollExpense || 0;
  const uniformsBadgesHiring = currentData?.pricing?.uniformsBadgesHiring || 0;
  const logisticCost = currentData?.pricing?.logisticCost || 0;
  const equipmentCost = currentData?.pricing?.equipmentCosts || 0;
  const materialsCost = currentData?.pricing?.materialsCost || 0;

  const radarChartOptions = {
    chart: {
      toolbar: {
        show: false
      },
      type: 'radar' as const
    },
    fill: {
      opacity: 0.2
    },
    markers: {
      size: 4
    },
    stroke: {
      width: 2
    },
    tooltip: {
      enabled: true
    },
    xaxis: {
      categories: [
        t('LEAD.CREATE.ANALYSIS.configurationCost'),
        t('LEAD.CREATE.ANALYSIS.SUPPLIES'),
        t('LEAD.CREATE.ANALYSIS.UNIFORMS_BADGES_HIRING'),
        t('LEAD.CREATE.ANALYSIS.PAYROLL_EXPENSE'),
        t('LEAD.CREATE.ANALYSIS.SUPERVISION_COST'),
        t('LEAD.CREATE.ANALYSIS.DIRECT_LABOR_COST'),
        t('LEAD.CREATE.ANALYSIS.suppliesCost'),
        t('LEAD.CREATE.ANALYSIS.logisticCost')
      ],
      labels: {
        style: {
          colors: new Array(6).fill(palette.text.primary)
        }
      }
    },
    yaxis: {
      show: false
    }
  };

  const radarChartData = [
    {
      data: [
        (configurationCosts / totalCosts) * 100,
        (supplies / totalCosts) * 100,
        (payrollExpense / totalCosts) * 100,
        (uniformsBadgesHiring / totalCosts) * 100,
        (supervisionCost / totalCosts) * 100,
        (directLaborCost / totalCosts) * 100,
        (suppliesCost / totalCosts) * 100,
        (logisticCost / totalCosts) * 100
      ],
      name: 'Analysis'
    }
  ];

  const barChartOptions = {
    chart: {
      height: 350,
      horizontal: true,
      stacked: true,
      toolbar: {
        show: false
      },
      type: 'bar' as const
    },
    colors: [
      '#B57DFF',
      palette.mainText[400],
      palette.accent?.[300],
      palette.warning[400],
      palette.success[400],
      palette.informative[400]
    ],
    dataLabels: {
      enabled: false
    },
    fill: {
      opacity: 0.2
    },
    markers: {
      size: 4
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        horizontal: true // Add this line to distribute colors
      }
    },
    stroke: {
      width: 2
    },
    tooltip: {
      enabled: true
    },
    xaxis: {
      categories: [
        t('LEAD.CREATE.ANALYSIS.configurationCosts'),
        t('LEAD.CREATE.ANALYSIS.ANNUAL_PRICE'),
        t('LEAD.CREATE.ANALYSIS.ANNUAL_GROSS_PROFIT'),
        t('LEAD.CREATE.ANALYSIS.ANNUAL_NET_PROFIT'),
        t('LEAD.CREATE.ANALYSIS.suppliesCost'),
        t('LEAD.CREATE.ANALYSIS.logisticCost')
      ]
    },
    yaxis: {
      show: true
    } // Add this line to specify colors
  };

  const barChartData = [
    {
      data: [
        currentData?.pricing?.configurationCosts,
        currentData?.pricing?.annualPrice,
        currentData?.pricing?.annualGrossProfit,
        currentData?.pricing?.annualNetProfit,
        currentData?.pricing?.suppliesCost,
        currentData?.pricing?.logisticCost
      ],
      name: 'Analysis'
    }
  ];

  const colorPercentages = [
    {
      color: palette.grey[500],
      percentage: (directLaborCost / totalPrice) * 100
    },
    {
      color: palette.grey[300],
      percentage: (supervisionCost / totalPrice) * 100
    },
    {
      color: '#B57DFF',
      percentage: (configurationCosts / totalPrice) * 100
    },
    {
      color: palette.success[500],
      percentage: (suppliesCost / totalPrice) * 100
    },
    {
      color: palette.informative[400],
      percentage: (logisticCost / totalPrice) * 100
    },
    {
      color: palette.success?.[300],
      percentage: (equipmentCost / totalPrice) * 100
    },
    {
      color: palette.success?.[100],
      percentage: (materialsCost / totalPrice) * 100
    },
    {
      color: palette.accent?.[200],
      percentage: (bidAmount / totalPrice) * 100
    }
  ];

  useEffect(() => {
    updateCalculator();
    return () => {
      // currentCalculatorMutate(null, true);
    };
  }, [values]);

  const { openInformationDrawer } = useInformationDrawerStore();

  const annualPriceNum = Number(currentData?.pricing?.annualPrice ?? 0);
  const monthlyPriceNum =
    annualPriceNum > 0 ? annualPriceNum / 12 : 0;

  return (
    <Box display="flex" flexDirection="column" gap="32px">
      {/* ── Hero: Bid Amount + controls ─────────────────────────────── */}
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
          <Box>
            <Typography
              color="secondary.400"
              sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
              variant="body_100"
            >
              Bid Amount
            </Typography>
            <Typography
              sx={{
                fontSize: { md: 40, xs: 28 },
                fontWeight: 700,
                lineHeight: 1.1,
                mt: 0.25
              }}
            >
              {getCurrency(isLoading ? 0 : annualPriceNum)}
            </Typography>
            <Typography color="secondary.400" sx={{ mt: 0.5 }} variant="body_100">
              per year · {getCurrency(isLoading ? 0 : monthlyPriceNum)} / month
            </Typography>
          </Box>
          {values?.contractInNegotiation && (
            <PreviousBidAmount
              companyInfo={values?.company}
              id={values?.previousProjectInformation?.leadId}
            />
          )}
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <RecomputeRatesButton
            leadId={values?.id}
            leadStatus={values?.status}
          />
          <IconButton onClick={() => handleOpenModal()}>
            <SettingsIcon />
          </IconButton>
          <Tooltip arrow title={`${t('INFORMATION_DRAWER.tooltipIcon')}`}>
            <IconButton
              color="inherit"
              onClick={() =>
                openInformationDrawer([
                  'bidAmount',
                  'monthlyProfit',
                  'profit',
                  'margin',
                  'costOfSale'
                ])
              }
              sx={{ height: '40px' }}
            >
              <HelpOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {tabIndex === 0 && (
        <>
          {/* ── Stats rail: 4 equal compact tiles ────────────────── */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                md: 'repeat(4, 1fr)',
                xs: 'repeat(2, 1fr)'
              }
            }}
          >
            {values?.projectType === 'Regular' && (
              <Tile
                isLoading={isLoading}
                key="analysis-tile-profitPerMonth"
                title={t('LEAD.CREATE.ANALYSIS.PROFIT_PER_MONTH')}
                type="Currency"
                value={getCurrency(currentData?.pricing?.pricePerMonth)}
              />
            )}
            <Tile
              isLoading={isLoading}
              key="analysis-tile-profitPerProject"
              title={t('LEAD.CREATE.ANALYSIS.PROFIT_PER_RPOJECT')}
              type="Currency"
              value={getCurrency(currentData?.pricing?.annualGrossProfit)}
            />
            <Tile
              isLoading={isLoading}
              key="analysis-tile-profitPercent"
              title={t('LEAD.CREATE.ANALYSIS.PROFIT_PERCENT')}
              type="Percentage"
              value={currentData?.pricing?.profitPercent}
            />
            <Tile
              isLoading={isLoading}
              key="analysis-tile-totalCost"
              title={t('LEAD.CREATE.ANALYSIS.TOTAL_COST')}
              type="Currency"
              value={getCurrency(currentData?.pricing?.totalCosts)}
            />
          </Box>

          {/* ── Margin Tier: own row, horizontal gauge ──────────── */}
          <MarginLadderTile
            currentMargin={
              currentData?.pricing?.profitPercent
                ? Number(currentData.pricing.profitPercent) / 100
                : undefined
            }
            monthlyValue={
              currentData?.pricing?.annualPrice
                ? Number(currentData.pricing.annualPrice) / 12
                : undefined
            }
          />
          {(() => {
            // Calc v3 Sub Analysis — needs hoursPerYear. First try the new
            // `totalHours` field exposed from calculatorProHelper's Total_Hrs
            // (populated from the margin calc). Fall back to summing from
            // service-time availability if for some reason that's 0.
            const hoursPerYearFromServiceTimes = (
              values?.serviceTimes ?? []
            ).reduce((acc: number, st: any) => {
              const weekly = Number(
                st?.availability?.generalInfo?.serviceTimeHoursPerWeek ??
                  st?.serviceTimeHoursPerWeek ??
                  0
              );
              const weeks = Number(
                st?.availability?.generalInfo?.operatingWeeks ??
                  st?.operatingWeeks ??
                  52
              );
              return acc + weekly * weeks;
            }, 0);
            const resolvedHoursPerYear =
              Number(
                currentData?.pricing?.totalHours ??
                  currentData?.pricing?.totalHoursPerYear ??
                  currentData?.pricing?.hoursPerYear ??
                  0
              ) || hoursPerYearFromServiceTimes;
            // Source of truth for price is `currentData.pricing.annualPrice`
            // (≈ the Bid Amount shown at the top). `pricePerMonth` in this
            // codebase is misnamed — it actually stores monthly *profit*,
            // not monthly price — so we can't compute annualPrice from it.
            const resolvedAnnualPrice = Number(
              currentData?.pricing?.annualPrice ?? 0
            );
            const resolvedMonthlyValue =
              resolvedAnnualPrice > 0
                ? resolvedAnnualPrice / 12
                : undefined;
            // Resolve contract duration from the lead's project window so
            // long contracts amplify the opportunity-cost penalty in the
            // BE engine.
            let resolvedDurationMonths: number | undefined;
            if (values?.projectStartDate && values?.projectEndDate) {
              const start = new Date(values.projectStartDate);
              const end = new Date(values.projectEndDate);
              const months =
                (end.getFullYear() - start.getFullYear()) * 12 +
                (end.getMonth() - start.getMonth()) +
                (end.getDate() >= start.getDate() ? 0 : -1);
              if (months > 0) resolvedDurationMonths = months;
            }
            return (
              <SubAnalysisPanel
                annualPrice={
                  resolvedAnnualPrice > 0 ? resolvedAnnualPrice : undefined
                }
                contractDurationMonths={resolvedDurationMonths}
                directLaborCost={Number(
                  currentData?.pricing?.directLaborCost ?? 0
                )}
                hoursPerYear={resolvedHoursPerYear}
                monthlyValue={resolvedMonthlyValue}
                serviceTimes={values?.serviceTimes}
                strategicValue={
                  (values?.client?.strategicValue as any) ?? 'standard'
                }
                totalCost={Number(currentData?.pricing?.totalCosts ?? 0)}
              />
            );
          })()}

          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: '8px',
              padding: '24px'
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <Box mb={4}>
                <Typography fontWeight="bold" variant="h6">
                  {t('LEAD.CREATE.ANALYSIS.PROJECT_COST_ANALYSIS_TITLE')}
                </Typography>
                <Typography>
                  {t('LEAD.CREATE.ANALYSIS.PROJECT_COST_ANALYSIS_DESCRIPTION')}
                </Typography>
              </Box>
              <Box alignItems="center" display="flex">
                <Tooltip arrow title={`${t('INFORMATION_DRAWER.tooltipIcon')}`}>
                  <IconButton
                    color="inherit"
                    onClick={() =>
                      openInformationDrawer([
                        'numberOfEmployees',
                        'employeeMedianAverage',
                        'grossProfitMarkup',
                        'logisticCost',
                        'materialsAndSuppliesCost',
                        'numberOfEmployees',
                        'overheadAndOtherCosts',
                        'supervisionHoursAssigned',
                        'totalSupervisionCost'
                      ])
                    }
                    sx={{ height: '40px', mr: 2 }}
                  >
                    <HelpOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box display="flex" mb={3} width="100%">
              {colorPercentages.map((segment, index) => (
                <Box
                  sx={{
                    backgroundColor: segment.color,
                    height: '24px',
                    width: `${segment.percentage}%`
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px'
              }}
            >
              {informationItems.map((item, index) => (
                <InformationItem
                  legendColor={item.legendColor || ''}
                  onEdit={item.onEdit}
                  title={item.title}
                  value={item.value}
                />
              ))}
            </Box>
          </Box>

          {openDrawer && (
            <GeneralDrawer
              onClose={() => {
                // currentCalculatorMutate(null, true);
                setOpenDrawer(false);
                setGetCalculator(true);
              }}
              width="70%"
            >
              <FormLead
                leadId={values?.id}
                leadInfo={values}
                onClose={() => {
                  setOpenDrawer(false);
                }}
                presetStep={presetStep}
                setValues={setValues}
              />
            </GeneralDrawer>
          )}
        </>
      )}

      {tabIndex === 1 && (
        <>
          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: '8px',
              display: 'flex',
              gap: '24px',
              padding: '24px'
            }}
          >
            <Box
              sx={{
                border: `1px solid ${palette.divider}`,
                borderRadius: '8px',
                padding: '24px',
                width: '50%'
              }}
            >
              <Chart
                height={400}
                options={radarChartOptions}
                series={radarChartData}
                type="radar"
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '38px',
                width: '50%'
              }}
            >
              <Tile
                isLoading={isLoading}
                key="analysis-tile-hoursSheduled"
                tileContainerPropSx={{ paddingBottom: theme.spacing(8) }}
                title={t('LEAD.CREATE.ANALYSIS.GROSS_MARGIN_AT_ACCOUNT')}
                type="Percentage"
                value={currentData?.pricing?.profitPercent}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: '24px'
                }}
              >
                <Tile
                  isLoading={isLoading}
                  key="analysis-tile-hoursSheduled"
                  tileContainerPropSx={{ paddingBottom: theme.spacing(8) }}
                  title={t('LEAD.CREATE.ANALYSIS.OVERHEAD_PERCENT')}
                  type="Percentage"
                  value={currentData?.analytics?.overheadPercent?.replace(
                    '%',
                    ''
                  )}
                />
                <Tile
                  isLoading={isLoading}
                  key="analysis-tile-hoursSheduled"
                  tileContainerPropSx={{ paddingBottom: theme.spacing(8) }}
                  title={t('LEAD.CREATE.ANALYSIS.MARK_UP_PERCENT')}
                  type="Percentage"
                  value={currentData?.analytics?.markUpPercent?.replace(
                    '%',
                    ''
                  )}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: '8px',
              padding: '24px'
            }}
          >
            <Box
              sx={{
                border: `1px solid ${palette.divider}`,
                borderRadius: '8px',
                padding: '24px'
                // width: '50%'
              }}
            >
              <Chart
                height={350}
                options={barChartOptions}
                series={barChartData}
                type="bar"
              />
            </Box>
          </Box>
        </>
      )}
      <Box display="flex" justifyContent="flex-end" width="100%">
        <Button
          onClick={() => saveStepper({ next: 6 })}
          sx={{ width: 304 }}
          variant="contained"
        >
          <Typography sx={{ textTransform: 'capitalize' }} variant="primary">
            {t('LEAD.CREATE.COST_CALCULATOR.goToProposalButton')}
          </Typography>
        </Button>
      </Box>

      {openModal && (
        <Container sx={{ margin: '40px', width: 'auto' }}>
          <Modal
            open
            onClose={() => {
              setOpenModal(false);
            }}
          >
            <ModalContainer>
              <Box>
                <Typography color="mainText.main" variant="secondary">
                  {t('COMPANY_SETTINGS.staticRateCosts')}
                </Typography>
                <SortableTable
                  disableSort
                  addManagement={false}
                  bodyKeys={['static', 'value', 'periodType']}
                  disabledTable={values.readOnly}
                  // handleDelete={setTaxId}
                  onChange={(newStaticCostList) => {
                    const updateStaticCostList = staticCosts.map(
                      (staticCost: any) => {
                        const newStaticCost = newStaticCostList.find(
                          (item: any) => item.id === staticCost.id
                        );
                        return newStaticCost
                          ? {
                              ...newStaticCost,
                              value: Number(newStaticCost.value)
                            }
                          : staticCost;
                      }
                    );
                    setStaticCosts(updateStaticCostList);

                    // setTaxes(updatedTaxes);
                  }}
                  type="static-input"
                  values={values?.staticCosts || []}
                />
              </Box>
              <ButtonContainerModal>
                <DrawerButton
                  color="error"
                  disabled={values.readOnly}
                  onClick={() => setOpenModal(false)}
                  sx={{ height: 46, width: 114 }}
                  variant="outlined"
                >
                  <Typography color="primary.red">
                    {t('MODAL.cancel')}
                  </Typography>
                </DrawerButton>
                <DrawerButton
                  color="primary"
                  disabled={values.readOnly}
                  onClick={() => {
                    setValues({
                      ...values,
                      staticCosts: values?.company?.staticCosts
                    });
                    setIsLoading(true);
                    setOpenModal(false);
                    setResetStaticCosts(true);
                  }}
                  sx={{ height: 46, width: 114 }}
                  variant="outlined"
                >
                  <Typography color="primary.main">{t('Reset')}</Typography>
                </DrawerButton>
                <DrawerButton
                  color="info"
                  disabled={values.readOnly}
                  onClick={() => {
                    setIsLoading(true);
                    setOpenModal(false);
                    setUpdateStaticCost(true);
                  }}
                  sx={{ height: 46, width: 114 }}
                  variant="contained"
                >
                  <Typography>{t('MODAL.save')}</Typography>
                </DrawerButton>
              </ButtonContainerModal>
            </ModalContainer>
          </Modal>
        </Container>
      )}
      {openInput && (
        <Container sx={{ margin: '40px', width: 'auto' }}>
          <Modal
            open
            onClose={() => {
              setOpenInput(false);
              setPresetInput('');
            }}
          >
            <ModalContainer>
              <Box>
                <Typography color="mainText.main" variant="secondary">
                  {(() => {
                    if (presetInput === 'laborRate') {
                      return t('LEAD.CREATE.ANALYSIS.EMPLOYEE_LABOR_RATE');
                    }
                    if (presetInput === 'suppliesCost') {
                      return t('LEAD.CREATE.ANALYSIS.suppliesCost');
                    }
                    return t('LEAD.CREATE.ANALYSIS.equipmentCost');
                  })()}
                </Typography>
                <RegularInput
                  customStyle={{ fontSize: '15px', width: '100%' }}
                  onChange={(value: any) => setInputValue(value)}
                  type={presetInput === 'laborRate' ? 'text' : 'number'}
                  value={inputValue}
                />
              </Box>
              <ButtonContainerModal>
                <DrawerButton
                  color="error"
                  disabled={values.readOnly}
                  onClick={() => setOpenInput(false)}
                  sx={{ height: 46, width: 114 }}
                  variant="outlined"
                >
                  <Typography color="primary.red">
                    {t('MODAL.cancel')}
                  </Typography>
                </DrawerButton>
                <DrawerButton
                  color="info"
                  disabled={values.readOnly}
                  onClick={() => {
                    let updatedValues = { ...values };
                    if (presetInput === 'laborRate') {
                      updatedValues.destinations = (
                        updatedValues.destinations || []
                      ).map((destination: any) => ({
                        ...destination,
                        serviceTimes: (destination.serviceTimes || []).map(
                          (serviceTime: any) => ({
                            ...serviceTime,
                            services: (serviceTime.services || []).map(
                              (service: any) => ({
                                ...service,
                                tasks: (service.tasks || []).map(
                                  (task: any) => ({
                                    ...task,
                                    mediaRate: inputValue,
                                    resources: (task.resources || []).map(
                                      (res: any) => ({
                                        ...res,
                                        rate: inputValue
                                      })
                                    ),
                                    technicians: (task.technicians || []).map(
                                      (tech: any) => ({
                                        ...tech,
                                        rate: inputValue
                                      })
                                    )
                                  })
                                )
                              })
                            )
                          })
                        )
                      }));
                    } else if (presetInput === 'suppliesCost') {
                      updatedValues = {
                        ...updatedValues,
                        suppliesCost: inputValue
                      };
                    } else if (presetInput === 'equipmentCost') {
                      updatedValues = {
                        ...updatedValues,
                        equipmentCost: inputValue
                      };
                    }
                    setValues(updatedValues);
                    setIsLoading(true);
                    if (presetInput === 'laborRate') {
                      setServiceTimeUpdate(true);
                    }
                    setOpenInput(false);
                    setPresetInput('');
                  }}
                  sx={{ height: 46, width: 114 }}
                  variant="contained"
                >
                  <Typography>{t('MODAL.save')}</Typography>
                </DrawerButton>
              </ButtonContainerModal>
            </ModalContainer>
          </Modal>
        </Container>
      )}
    </Box>
  );
};

export default Analysis;
